import { formatCurrency } from "@angular/common";
import { Component, Inject, Input, LOCALE_ID, OnDestroy, signal } from "@angular/core";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { HttpErrorResponse } from "@angular/common/http";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { LoggingService, NavigationService, UrlService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { AppLinkParms, FilterItem, IChartCriteria, } from "@tylertech/tyler-api-types";

@Component({
    selector: "purchasing-po-status-chart",
    templateUrl: "./poStatusChart.tmpl.html",
    styleUrls: [
        "./poStatusChart.component.scss"
    ],
    standalone: false
})
export class PoStatusChartComponent implements OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;

    private readonly _chartId: string;

    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;

    ChartTitle: string = "Open Purchase Order Status";
    isLoaded: boolean;
    total: string;

    constructor(
        @Inject(LOCALE_ID) private readonly locale: string,
        $chartManagerService: ChartManagerService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartService: ChartService,
        private readonly $loggingService: LoggingService,
        private readonly $urlService: UrlService,
        private readonly $navigationService: NavigationService,
        private readonly $intentService: IntentService,
    )
    {
        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get Department(): string
    {
        const depts = this.chartCriteria?.Parameters?.find(x => x.Id === "PurchaseOrderStatusDepartment")?.Value || null;
        if (!depts)
        {
            return "";
        }

        let department = "";
        const departments = depts.split(",");
        let options = [];
        options = this.chartCriteria?.Parameters?.find(x => x.Id === "PurchaseOrderStatusDepartment")?.Options;
        for (const opt of options)
        {
            if (departments.indexOf(opt.Value) > -1)
            {
                department = department + opt.Value.trim() + ", ";
            }
        }
        return department.trim()
            .slice(0, -1);
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private updateChartConfiguration(): void
    {
        const amount = this.chartCriteria.ChartDatas[0].Data.reduce((a, v) => a + v.Total, 0);
        this.total = "$" + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                scales: {
                    y: {
                        type: "linear",
                        display: true,
                        grid: { display: true },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(
                                    value > -10 && value < 10
                                        ? value.toFixed(2)
                                        : value.toFixed(0));
                            },
                        },
                    }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                const label = tooltipItem.label;
                                const count = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Count;
                                const value = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Total;
                                return `${label} ( Count: ${count}; Total: ${formatCurrency(value, this.locale, "$")} )`;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (Utilities.IsNullOrUndefined(element))
                    {
                        return;
                    }
                    const index = element.index;
                    const filter: FilterItem[] = [
                        {
                            Key: "Status",
                            Value: this.chartCriteria.ChartDatas[0].Labels[index]
                            //Value: this.chart.options.legend.labels[index],
                        },
                        {
                            Key: "Department",
                            Value: this.Department,
                        },
                        {
                            Key: "ChartType",
                            Value: "POStatusChart",
                        }
                    ];
                    const app = "purchaseorders";
                    const parms: AppLinkParms = {
                        App: app,
                        Parms: filter,
                    };
                    this.$urlService
                        .GetAppLink(parms)
                        .then(x => x.Result)
                        .then((result: string) =>
                        {
                            const index = result.indexOf("?");
                            if (result.includes("apps/form"))
                            {
                                this.$navigationService.navigateToApp(result);
                            }
                            else if (index > 0)
                            {
                                this.$intentService.resolve("PurchaseOrders", "Search", {
                                    querystring: result.substring(index, result.length),
                                });
                            }
                        });
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [{
                    type: "bar",
                    data: this.chartCriteria.ChartDatas[0].Data.map(x => x.Total),
                    backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data.length),
                }],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
